import { forwardRef, type CSSProperties } from 'react';
import { duration } from '../scale.js';
import { useStyleOnce, REDUCED_MOTION } from './useStyleOnce.js';

export interface SpinnerProps {
  size?: number;
  stroke?: number;
  color?: string;
  /* Accessible name. Omitted, the spinner is decorative: its host (Button aria-busy, ProgressRing) carries the state. */
  label?: string;
  className?: string;
  style?: CSSProperties;
}

const CSS = `@keyframes rvSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
[data-rv-spinner] { animation: rvSpin ${duration.spin}ms linear infinite; transform-origin: 50% 50%; }
${REDUCED_MOTION} { [data-rv-spinner] { animation: none; } }`;

/* ProgressRing's indeterminate mode at Button scale: a 25% arc on no track. */
export const Spinner = forwardRef<SVGSVGElement, SpinnerProps>(function Spinner(
  { size = 16, stroke = 2, color = 'currentColor', label, className, style },
  ref,
) {
  useStyleOnce('spinner', CSS);
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const a11y = label ? { role: 'progressbar' as const, 'aria-label': label } : { 'aria-hidden': true, focusable: false };
  return (
    <svg
      ref={ref}
      data-rv-spinner=""
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={className}
      style={{ display: 'block', flexShrink: 0, ...style }}
      {...a11y}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={`${c * 0.25} ${c}`}
      />
    </svg>
  );
});
